import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Divider
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import Parse from 'parse';

const EMPTY = { currentPassword: '', newPassword: '', confirmPassword: '' };

// Defined at module scope (NOT inside ChangePassword) so its component identity
// is stable across renders. A component declared inside the parent is recreated
// on every keystroke, which remounts the input and drops focus/caret position.
const PasswordField = ({ name, label, value, error, showPassword, onChange, onToggle }) => (
  <Box>
    <Typography sx={labelSx}>{label} *</Typography>
    <TextField
      fullWidth
      name={name}
      type={showPassword ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      error={!!error}
      helperText={error}
      placeholder={`Enter ${label.toLowerCase()}`}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <LockIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={onToggle} edge="end" sx={{ color: 'rgba(255,255,255,0.4)' }}>
              {showPassword ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </IconButton>
          </InputAdornment>
        )
      }}
      sx={passwordFieldSx}
    />
  </Box>
);

const ChangePassword = () => {
  const [formData, setFormData] = useState(EMPTY);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const toggleShow = (field) => setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));

  const validate = () => {
    const e = {};
    if (!formData.currentPassword) e.currentPassword = 'Current password is required';
    if (!formData.newPassword) e.newPassword = 'New password is required';
    else if (formData.newPassword.length < 6)
      e.newPassword = 'Password must be at least 6 characters';
    else if (formData.currentPassword && formData.currentPassword === formData.newPassword)
      e.newPassword = 'New password must be different from current password';
    if (!formData.confirmPassword) e.confirmPassword = 'Confirm password is required';
    else if (formData.newPassword !== formData.confirmPassword)
      e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      if (!sessionToken) throw new Error('No session token found. Please login again.');
      await Parse.User.become(sessionToken);
      const response = await Parse.Cloud.run('changeOperatorPassword', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });
      if (response.success) {
        // Changing the password revokes the old session; adopt the fresh token
        // the backend returns so the user stays logged in (no "Invalid session
        // token" on the next request).
        if (response.sessionToken) {
          localStorage.setItem('sessionToken', response.sessionToken);
          await Parse.User.become(response.sessionToken);
        }
        setFormData(EMPTY);
        setShowPasswords({ current: false, new: false, confirm: false });
        setSnackbar({ open: true, message: 'Password changed successfully', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to change password',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData(EMPTY);
    setErrors({});
    setShowPasswords({ current: false, new: false, confirm: false });
  };

  return (
    <Box sx={{ py: 2, px: 2, maxWidth: 520 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Card
        sx={{
          backgroundColor: '#1E1E1E',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '8px'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ color: '#FFF', fontSize: 18, fontWeight: 600, mb: 1 }}>
            Change Password
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', mb: 3 }}>
            Update your account password securely.
          </Typography>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 3 }} />

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <PasswordField
                name="currentPassword"
                label="Current Password"
                value={formData.currentPassword}
                error={errors.currentPassword}
                showPassword={showPasswords.current}
                onChange={handleChange}
                onToggle={() => toggleShow('current')}
              />
              <PasswordField
                name="newPassword"
                label="New Password"
                value={formData.newPassword}
                error={errors.newPassword}
                showPassword={showPasswords.new}
                onChange={handleChange}
                onToggle={() => toggleShow('new')}
              />
              <PasswordField
                name="confirmPassword"
                label="Confirm Password"
                value={formData.confirmPassword}
                error={errors.confirmPassword}
                showPassword={showPasswords.confirm}
                onChange={handleChange}
                onToggle={() => toggleShow('confirm')}
              />

              <Box sx={{ display: 'flex', gap: 2, pt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    backgroundColor: '#FFC107',
                    color: '#000',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 3,
                    '&:hover': { backgroundColor: '#FFD54F' },
                    '&:disabled': {
                      backgroundColor: 'rgba(255,193,7,0.4)',
                      color: 'rgba(0,0,0,0.4)'
                    }
                  }}
                >
                  {loading ? <CircularProgress size={18} sx={{ color: '#000', mr: 1 }} /> : null}
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  disabled={loading}
                  onClick={handleCancel}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'rgba(255,255,255,0.6)',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: 'rgba(255,255,255,0.4)',
                      backgroundColor: 'rgba(255,255,255,0.05)'
                    }
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

const labelSx = { color: 'rgba(255,255,255,0.6)', fontSize: '13px', mb: 1, fontWeight: 500 };

const passwordFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#2B2B2B',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&.Mui-focused fieldset': { borderColor: '#FFC107' }
  },
  '& .MuiOutlinedInput-input': { color: '#fff' },
  '& .MuiFormHelperText-root': { color: '#f44336' }
};

export default ChangePassword;
